import { useMemo, useState } from 'react';
import type { UserBrief } from '../types';

export function buildHierarchy(users: UserBrief[]) {
  const byId = new Map(users.map(user => [user.id, user]));
  const children = new Map<string, UserBrief[]>();
  const roots: UserBrief[] = [];
  for (const user of users) {
    if (user.responsabile_id && byId.has(user.responsabile_id) && user.responsabile_id !== user.id) {
      const siblings = children.get(user.responsabile_id) || [];
      siblings.push(user);
      children.set(user.responsabile_id, siblings);
    } else roots.push(user);
  }
  const compare = (a: UserBrief, b: UserBrief) => (a.role === 'manager' ? -1 : 0) - (b.role === 'manager' ? -1 : 0) || a.name.localeCompare(b.name, 'it');
  roots.sort(compare);
  children.forEach(items => items.sort(compare));
  // Include legacy cycles once, without hiding users or recursing forever.
  const reachable = new Set<string>();
  const visit = (id: string) => {
    if (reachable.has(id)) return;
    reachable.add(id);
    (children.get(id) || []).forEach(child => visit(child.id));
  };
  roots.forEach(root => visit(root.id));
  for (const user of users) if (!reachable.has(user.id)) { roots.push(user); visit(user.id); }
  return { roots, children };
}

export default function UserHierarchy({ users }: { users: UserBrief[] }) {
  const { roots, children } = useMemo(() => buildHierarchy(users), [users]);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const renderNode = (user: UserBrief, ancestors: Set<string>): React.ReactNode => {
    if (ancestors.has(user.id)) return null;
    const reports = (children.get(user.id) || []).filter(child => !ancestors.has(child.id));
    const next = new Set(ancestors).add(user.id);
    const open = !collapsed.has(user.id);
    return <li key={user.id} className="py-1">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white p-3">
        {reports.length > 0 && <button type="button" aria-expanded={open} aria-label={`${open ? 'Comprimi' : 'Espandi'} ${user.name}`} onClick={() => setCollapsed(previous => { const updated = new Set(previous); if (open) updated.add(user.id); else updated.delete(user.id); return updated; })} className="rounded px-2 py-1 hover:bg-gray-100">{open ? '−' : '+'}</button>}
        <span className="font-medium">{user.name}</span>
        <span className="text-xs text-gray-500">{user.role === 'manager' ? 'Area Manager' : user.role === 'commerciale' ? 'Agente' : user.role}</span>
        {!user.is_active && <span className="text-xs text-red-700">Inattivo</span>}
        <span className="text-xs text-gray-500">{user.email}</span>
        {reports.length > 0 && <span className="text-xs text-blue-700">{reports.length} subordinati diretti</span>}
      </div>
      {open && reports.length > 0 && <ul className="ml-5 border-l border-gray-300 pl-4">{reports.map(child => renderNode(child, next))}</ul>}
    </li>;
  };
  return <section aria-label="Gerarchia utenti" className="mb-6 rounded-lg bg-gray-50 p-4">
    <h2 className="font-semibold text-gray-900">Gerarchia Area Manager → Agenti</h2>
    <p className="mb-3 text-sm text-gray-600">Ogni responsabile vede i dati dei propri subordinati, anche nei livelli successivi. La vista include gli utenti inattivi.</p>
    {roots.length ? <ul>{roots.map(user => renderNode(user, new Set()))}</ul> : <p className="text-sm text-gray-500">Nessun utente disponibile.</p>}
  </section>;
}
